import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { Box } from '@rsos/components/capstone/base';
import LocationSummary from '@rsos/components/capstone/common/Map/MapCard/LocationSummary';
import { StyledContentBox } from '@rsos/components/capstone/common/Map/MapCard/MapCard';
import MapCardHeader from '@rsos/components/capstone/common/Map/MapCard/MapCardHeader';
import { captureExceptionWithScope } from '@rsos/utils/sentry';
import usePrevious from '@rsos/utils/usePrevious';
import { EllingtonCard } from '../AdditionalData/EllingtonCard/EllingtonCard';
import ErrorBoundary from '../AdditionalData/EllingtonCard/ErrorBoundary';
import { SpatialLayerCardContainer } from './MapCard.styles';
import usePatchedDisplaySchema from './usePatchedDisplaySchema';

export const StyledMapCard = styled(Box)`
  width: ${MAP_CARD_WIDTH}px;
`;

const MAP_CARD_WIDTH = 275; // design calls for 305, subtracted 30px padding

const StaticLayerCard = () => {
  const { mapCardData } = useSelector(state => state.mapCard);
  const prevMapCardData = usePrevious(mapCardData);

  const address = useMemo(
    () => mapCardData?.spatialObject?.attributes?.address,
    [mapCardData],
  );

  const cardData = useMemo(() => mapCardData?.spatialObject?.attributes, [
    mapCardData,
  ]);

  const patchedDisplaySchema = usePatchedDisplaySchema();

  const coordinates = {
    latitude: mapCardData?.spatialObject?.center?.coordinates[1],
    longitude: mapCardData?.spatialObject?.center?.coordinates[0],
  };

  useEffect(() => {
    const isSameSpatialObj =
      prevMapCardData?.layerData.source !== mapCardData?.layerData.source &&
      prevMapCardData?.spatialObject.id !== mapCardData?.spatialObject.id;
    if (
      (isSameSpatialObj && !patchedDisplaySchema) ||
      JSON.stringify(patchedDisplaySchema) === '{}'
    ) {
      captureExceptionWithScope('Error: Spatial layer missing display schema', {
        layerData: JSON.stringify(mapCardData.layerData),
      });
    }
  }, [prevMapCardData, patchedDisplaySchema, mapCardData]);

  const fallbackTitle =
    mapCardData?.spatialObject?.name || mapCardData?.layerData?.title;

  return (
    <SpatialLayerCardContainer>
      <StyledMapCard>
        <MapCardHeader
          data={mapCardData.spatialObject.attributes}
          displaySchema={patchedDisplaySchema}
          layer={mapCardData.layerData}
          title={fallbackTitle}
          wrapHeader
        />
      </StyledMapCard>
      <Box m={3}>
        <LocationSummary
          location={address}
          coordinates={coordinates}
          overridden
        />

        {JSON.stringify(patchedDisplaySchema) !== '{}' && (
          <StyledContentBox
            shouldRender={patchedDisplaySchema?.propertiesOrder?.length}
          >
            <ErrorBoundary>
              <EllingtonCard
                data={cardData}
                display_schema={patchedDisplaySchema}
                createdTime={mapCardData.spatialObject.effective_date}
                shouldDisplayBlankFields
                source={mapCardData.layerData.source}
                borderless
                noHeader
              />
            </ErrorBoundary>
          </StyledContentBox>
        )}
      </Box>
    </SpatialLayerCardContainer>
  );
};

export default StaticLayerCard;
